import { toCalendarIsoDay } from "./tradeDashboardDates";
import {
  isCancelledTrade,
  getPaymentReceivedIso,
  tradeBelongsToAgent,
  commissionAmountForTrade,
  eftBelongsToAgent,
} from "./dashboardStatsUtils";

function isAgentDraft(trade) {
  return (
    trade?.createdByRole === "agent" &&
    !trade?.lockedBySuperUser &&
    trade?.workflowStatus !== "approved"
  );
}

function countableTrade(trade, appUser) {
  if (isCancelledTrade(trade)) return false;
  if (appUser?.role === "agent") {
    return tradeBelongsToAgent(trade, appUser);
  }
  return !isAgentDraft(trade);
}

export function currentMonthCursor(now = new Date()) {
  return { year: now.getFullYear(), month: now.getMonth() };
}

export function shiftMonth(cursor, delta) {
  const d = new Date(cursor.year, cursor.month + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() };
}

export function monthKey(cursor) {
  return `${cursor.year}-${String(cursor.month + 1).padStart(2, "0")}`;
}

export function monthTitle(cursor) {
  return new Date(cursor.year, cursor.month, 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

export function daysInMonth(cursor) {
  return new Date(cursor.year, cursor.month + 1, 0).getDate();
}

function emptyDaySeries(cursor) {
  const count = daysInMonth(cursor);
  const prefix = monthKey(cursor);
  return Array.from({ length: count }, (_, i) => {
    const day = i + 1;
    return {
      key: `${prefix}-${String(day).padStart(2, "0")}`,
      label: String(day),
      value: 0,
    };
  });
}

export function buildCommissionReceivedForMonth(trades, cursor, appUser) {
  const series = emptyDaySeries(cursor);
  const byKey = Object.fromEntries(series.map((d) => [d.key, 0]));
  const prefix = monthKey(cursor);
  const agentScoped = appUser?.role === "agent";
  for (const trade of trades || []) {
    if (!countableTrade(trade, appUser)) continue;
    const receipts = !agentScoped ? trade.commissionReceipts : null;
    if (Array.isArray(receipts) && receipts.length) {
      for (const rec of receipts) {
        const iso = String(rec?.dateIso || "").trim();
        if (!iso || !iso.startsWith(prefix) || byKey[iso] == null) continue;
        byKey[iso] += Number(rec.amount) || 0;
      }
      continue;
    }
    const paymentIso = getPaymentReceivedIso(trade);
    if (!paymentIso || !paymentIso.startsWith(prefix)) continue;
    if (byKey[paymentIso] == null) continue;
    byKey[paymentIso] += commissionAmountForTrade(trade, appUser);
  }
  return series.map((d) => ({ ...d, value: byKey[d.key] || 0 }));
}

function eftPaidOutAmount(eft) {
  return Number(eft?.amount) || 0;
}

function eftDayKey(eft) {
  return toCalendarIsoDay(eft?.chequeDate || eft?.date || eft?.createdAt);
}

function canonicalEftType(v) {
  return String(v || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
}

/** Money into trust — never count as "paid out / issued". */
const CT_INBOUND_TYPES = new Set([
  canonicalEftType("CommissionReceived"),
  canonicalEftType("GeneralToCommissionTransfer"),
]);

const RET_INBOUND_TYPES = new Set([canonicalEftType("TrustDeposit")]);

function isCommissionTrustPayoutEft(eft) {
  return !CT_INBOUND_TYPES.has(canonicalEftType(eft?.type));
}

function isRealEstateTrustPayoutEft(eft) {
  return !RET_INBOUND_TYPES.has(canonicalEftType(eft?.type));
}

export function buildEftsPaidOutForMonth(
  commissionEfts,
  realEstateEfts,
  generalEfts,
  cursor,
  appUser,
  agentTradeIds,
) {
  const series = emptyDaySeries(cursor);
  const byKey = Object.fromEntries(series.map((d) => [d.key, 0]));
  const prefix = monthKey(cursor);
  const agentScoped = appUser?.role === "agent";

  const add = (list, include) => {
    for (const eft of list || []) {
      if (eft?.reversed) continue;
      if (include && !include(eft)) continue;
      if (agentScoped && !eftBelongsToAgent(eft, appUser, agentTradeIds)) {
        continue;
      }
      const key = eftDayKey(eft);
      if (!key || !key.startsWith(prefix) || byKey[key] == null) continue;
      byKey[key] += eftPaidOutAmount(eft);
    }
  };

  add(commissionEfts, isCommissionTrustPayoutEft);
  add(realEstateEfts, isRealEstateTrustPayoutEft);
  if (!agentScoped) {
    add(generalEfts);
  }

  return series.map((d) => ({ ...d, value: byKey[d.key] || 0 }));
}

export function formatChartAxis(value) {
  const n = Number(value) || 0;
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${Math.round(n / 1000)}k`;
  return `$${Math.round(n)}`;
}
